import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable } from "@/components/common/DataTable";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { title } from "@/lib/format";

const SOURCES = [
  { value: "first_name", label: "First name" },
  { value: "last_name", label: "Last name" },
  { value: "contact_name", label: "Full name" },
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "company", label: "Company" },
  { value: "campaign_name", label: "Campaign" },
  { value: "product", label: "Product" },
  { value: "country", label: "Country" },
  { value: "language", label: "Language" },
  { value: "interest_level", label: "Interest level" },
  { value: "interest_score", label: "Interest score" },
  { value: "lead_category", label: "Lead category" },
  { value: "summary", label: "Summary" },
  { value: "pipeline_stage", label: "Pipeline stage" },
  { value: "id", label: "Lead id" },
  { value: "assignee_id", label: "Assignee id (chosen when sending)" },
  { value: "assignee_label", label: "Assignee name (chosen when sending)" },
  { value: "static", label: "Static value" },
];

const emptyMap = (target, source, value = "", valueType = "text") => ({ target, source, value, value_type: valueType });

const emptyForm = () => ({
  name: "",
  enabled: true,
  url: "",
  method: "POST",
  auth_type: "bearer",
  auth_header: "X-Api-Key",
  token: "",
  timeout_seconds: 15,
  body_format: "json",
  field_mappings: [
    emptyMap("name", "first_name"),
    emptyMap("phone", "phone"),
    emptyMap("assignee", "assignee_id"),
  ],
  assignees: [],
});

export default function CrmIntegrations() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const list = useQuery({ queryKey: ["crm-integrations"], queryFn: () => get("/crm-integrations") });

  const save = useMutation({
    mutationFn: (payload) => (editing
      ? api.put(`/crm-integrations/${editing.id}`, payload)
      : api.post("/crm-integrations", payload)),
    onSuccess: () => {
      toast.success(editing ? "CRM integration updated" : "CRM integration created");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["crm-integrations"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/crm-integrations/${id}`),
    onSuccess: () => {
      toast.success("CRM integration deleted");
      queryClient.invalidateQueries({ queryKey: ["crm-integrations"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const test = useMutation({
    mutationFn: (id) => api.post(`/crm-integrations/${id}/test`),
    onSuccess: (res) => {
      setTestResult(res.data);
      if (res.data.ok) toast.success(`CRM accepted the test (${res.data.status})`);
      else toast.error(res.data.error || "CRM test failed");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const startCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setTestResult(null);
    setOpen(true);
  };

  const startEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      enabled: row.enabled !== false,
      url: row.url || "",
      method: row.method || "POST",
      auth_type: row.auth_type || "none",
      auth_header: row.auth_header || "X-Api-Key",
      token: "",
      timeout_seconds: row.timeout_seconds || 15,
      body_format: row.body_format || "json",
      field_mappings: (row.field_mappings || []).map((rowMap) => ({
        target: rowMap.target || "",
        source: rowMap.source || "first_name",
        value: rowMap.value || "",
        value_type: rowMap.value_type || "text",
      })),
      assignees: (row.assignees || []).map((rowAssignee) => ({ ...rowAssignee })),
    });
    setTestResult(null);
    setOpen(true);
  };

  const setMapping = (index, patch) => {
    setForm((current) => ({
      ...current,
      field_mappings: current.field_mappings.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  };

  const setAssignee = (index, patch) => {
    setForm((current) => ({
      ...current,
      assignees: current.assignees.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  };

  const submit = () => {
    const mappings = form.field_mappings.filter((row) => row.target.trim());
    const assignees = form.assignees.filter((row) => row.id.trim() || row.label.trim());
    if (assignees.some((row) => !row.id.trim() || !row.label.trim())) {
      toast.error("Each assignee needs an id and a label");
      return;
    }
    save.mutate({
      name: form.name.trim(),
      enabled: form.enabled,
      url: form.url.trim(),
      method: form.method,
      auth_type: form.auth_type,
      auth_header: form.auth_header.trim() || "X-Api-Key",
      token: form.token.trim() || undefined,
      timeout_seconds: Number(form.timeout_seconds) || 15,
      body_format: form.body_format || "json",
      field_mappings: mappings.map((row) => ({
        target: row.target.trim(),
        source: row.source,
        value: row.source === "static" ? row.value : null,
        value_type: row.source === "static" ? (row.value_type || "text") : null,
      })),
      assignees: assignees.map((row) => ({
        id: row.id.trim(),
        label: row.label.trim(),
        role: row.role || "agent",
      })),
    });
  };

  return (
    <>
      <PageHeader
        testId="crm-page"
        title="CRM integrations"
        subtitle="Sinon sends qualified leads to the Virgo partner API. Paste the affiliate token, then enable it."
        actions={isAdmin ? (
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={startCreate} data-testid="crm-create">
            <Plus className="mr-1 h-4 w-4" /> Add integration
          </Button>
        ) : null}
      />

      <Section testId="crm-list-section" title={`${list.data?.length ?? 0} integrations`}>
        <DataTable
          testId="crm-table"
          rowTestId="crm-row"
          loading={list.isLoading}
          rows={list.data || []}
          empty={{ title: "No CRM integrations", description: "Add a URL, method, token, and field map so qualified leads can be pushed to your CRM." }}
          columns={[
            { key: "name", label: "Name", render: (r) => <span className="font-medium text-slate-800">{r.name}</span> },
            { key: "url", label: "URL", render: (r) => <span className="font-mono text-xs">{r.url || "—"}</span> },
            { key: "method", label: "Method", render: (r) => `${r.method || "POST"} · ${r.body_format === "form" ? "form" : "json"}` },
            { key: "auth_type", label: "Auth", render: (r) => (r.auth_type === "api_key" ? `Header ${r.auth_header || "X-Api-Key"}` : title(r.auth_type || "none")) },
            { key: "enabled", label: "Enabled", render: (r) => {
              if (!r.enabled && r.auth_type !== "none" && !r.token_set) return "Disabled · add token";
              return r.enabled ? "Yes" : "No";
            } },
            { key: "assignees", label: "Assignees", render: (r) => (r.assignees || []).length },
            { key: "fields", label: "Fields", render: (r) => (r.field_mappings || []).length },
            ...(isAdmin ? [{
              key: "actions",
              label: "",
              render: (r) => (
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => test.mutate(r.id)} disabled={test.isPending} data-testid={`crm-test-${r.id}`}>Test</Button>
                  <Button size="sm" variant="outline" onClick={() => startEdit(r)} data-testid={`crm-edit-${r.id}`}>Edit</Button>
                  <Button size="sm" variant="outline" className="text-rose-700" onClick={() => setPendingDelete(r)} data-testid={`crm-delete-${r.id}`}>Delete</Button>
                </div>
              ),
            }] : []),
          ]}
        />
      </Section>

      {testResult ? (
        <Section testId="crm-test-result" title={testResult.ok ? "Last test succeeded" : "Last test failed"}>
          <pre className="max-h-48 overflow-auto p-4 text-xs text-slate-700" data-testid="crm-test-snippet">
            {`HTTP ${testResult.status ?? "—"}\n${testResult.error || testResult.snippet || ""}`}
          </pre>
        </Section>
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit CRM integration" : "New CRM integration"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input data-testid="crm-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sales CRM" />
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Switch checked={form.enabled} onCheckedChange={(checked) => setForm({ ...form, enabled: checked })} data-testid="crm-enabled" />
                <Label>Enabled</Label>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>URL</Label>
                <Input data-testid="crm-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://crm.example.com/api/leads" />
              </div>
              <div className="space-y-1.5">
                <Label>Method</Label>
                <Select value={form.method} onValueChange={(value) => setForm({ ...form, method: value })}>
                  <SelectTrigger data-testid="crm-method"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {["POST", "PUT", "PATCH"].map((method) => <SelectItem key={method} value={method}>{method}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Body</Label>
                <Select value={form.body_format || "json"} onValueChange={(value) => setForm({ ...form, body_format: value })}>
                  <SelectTrigger data-testid="crm-body"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="json">JSON</SelectItem>
                    <SelectItem value="form">Form (Sinon)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Timeout (seconds)</Label>
                <Input data-testid="crm-timeout" type="number" min={1} max={60} value={form.timeout_seconds} onChange={(e) => setForm({ ...form, timeout_seconds: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Auth</Label>
                <Select value={form.auth_type} onValueChange={(value) => setForm({ ...form, auth_type: value })}>
                  <SelectTrigger data-testid="crm-auth"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="bearer">Bearer token</SelectItem>
                    <SelectItem value="api_key">API key header</SelectItem>
                    <SelectItem value="basic">HTTP basic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.auth_type === "api_key" ? (
                <div className="space-y-1.5">
                  <Label>Header name</Label>
                  <Input data-testid="crm-auth-header" value={form.auth_header} onChange={(e) => setForm({ ...form, auth_header: e.target.value })} placeholder="X-Api-Key" />
                </div>
              ) : <div />}
              {form.auth_type !== "none" ? (
                <div className="space-y-1.5 md:col-span-2">
                  <Label>{form.auth_type === "basic" ? "Username:password" : "Token"}</Label>
                  <Input
                    data-testid="crm-token"
                    type="password"
                    value={form.token}
                    autoComplete="new-password"
                    onChange={(e) => setForm({ ...form, token: e.target.value })}
                    placeholder={editing?.token_set ? `Saved ${editing.token_preview || "token"} — leave blank to keep it` : (form.auth_header === "token" ? "Affiliate API token" : "Paste the token")}
                  />
                  {form.auth_header === "token" ? (
                    <p className="text-xs text-slate-500">Sinon reads this from a header named token. An Authorization Bearer header is ignored.</p>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-medium text-slate-800">Field map</p>
                <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, field_mappings: [...form.field_mappings, emptyMap("", "phone")] })} data-testid="crm-add-field">
                  Add field
                </Button>
              </div>
              <div className="space-y-2">
                {form.field_mappings.map((row, index) => (
                  <div key={index} className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
                    <Input data-testid={`crm-field-target-${index}`} value={row.target} placeholder="CRM field" onChange={(e) => setMapping(index, { target: e.target.value })} />
                    <Select value={row.source} onValueChange={(value) => setMapping(index, { source: value })}>
                      <SelectTrigger data-testid={`crm-field-source-${index}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {SOURCES.map((source) => <SelectItem key={source.value} value={source.value}>{source.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {row.source === "static" ? (
                      <div className="flex gap-2">
                        <Select value={row.value_type || "text"} onValueChange={(value) => setMapping(index, { value_type: value, value: value === "boolean" ? "false" : row.value })}>
                          <SelectTrigger className="w-28" data-testid={`crm-field-type-${index}`}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="text">Text</SelectItem>
                            <SelectItem value="boolean">Boolean</SelectItem>
                          </SelectContent>
                        </Select>
                        {row.value_type === "boolean" ? (
                          <Select value={String(row.value) === "true" ? "true" : "false"} onValueChange={(value) => setMapping(index, { value })}>
                            <SelectTrigger data-testid={`crm-field-value-${index}`}><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="false">false</SelectItem>
                              <SelectItem value="true">true</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input data-testid={`crm-field-value-${index}`} value={row.value} placeholder="Static value" onChange={(e) => setMapping(index, { value: e.target.value })} />
                        )}
                      </div>
                    ) : <div className="hidden md:block" />}
                    <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, field_mappings: form.field_mappings.filter((_, i) => i !== index) })} aria-label="Remove field">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-medium text-slate-800">Supervisors and agents</p>
                <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, assignees: [...form.assignees, { id: "", label: "", role: "agent" }] })} data-testid="crm-add-assignee">
                  Add person
                </Button>
              </div>
              <p className="mb-2 text-xs text-slate-500">These ids are what the CRM expects. The person sending a lead picks one, and assignee fields in the map receive that id and name.</p>
              <div className="space-y-2">
                {form.assignees.map((row, index) => (
                  <div key={index} className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_140px_auto]">
                    <Input data-testid={`crm-assignee-id-${index}`} value={row.id} placeholder="CRM id" onChange={(e) => setAssignee(index, { id: e.target.value })} />
                    <Input data-testid={`crm-assignee-label-${index}`} value={row.label} placeholder="Name" onChange={(e) => setAssignee(index, { label: e.target.value })} />
                    <Select value={row.role || "agent"} onValueChange={(value) => setAssignee(index, { role: value })}>
                      <SelectTrigger data-testid={`crm-assignee-role-${index}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="supervisor">Supervisor</SelectItem>
                        <SelectItem value="agent">Agent</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, assignees: form.assignees.filter((_, i) => i !== index) })} aria-label="Remove assignee">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            {editing ? (
              <Button type="button" variant="outline" disabled={test.isPending} onClick={() => test.mutate(editing.id)} data-testid="crm-dialog-test">
                Test saved
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={save.isPending} onClick={submit} data-testid="crm-save">
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(next) => { if (!next) setPendingDelete(null); }}
        title="Delete CRM integration"
        description={pendingDelete ? `Delete ${pendingDelete.name}? Leads already sent keep their last sync status.` : ""}
        confirmLabel="Delete"
        destructive
        onConfirm={() => remove.mutateAsync(pendingDelete.id)}
      />
    </>
  );
}
